import { useAppDispatch, useAppSelector } from '@/app/reduxHooks'
import { useDownloadDestinationFileMutation } from '@/features/adp/mutations/useDownloadMutations'
import { closeOverlay, type DownloadOutputOverlayData } from '@/features/adp/state/overlaySlice'
import { Button } from '@/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/ui/dialog';
import { toast } from 'sonner';
import styles from './DownloadOutputModal.module.scss';
import DownloadIcon from "@/assets/icons/download.svg?react";

function isDownloadOutputOverlayData(value: unknown): value is DownloadOutputOverlayData {
  if (!value || typeof value !== 'object') return false
  return 'downloadFolderName' in value || 'downloadTargetTableId' in value
}

const DownloadOutputModal = () => {
  const dispatch = useAppDispatch()
  const { isOpen, type, data } = useAppSelector(state => state.overlay);

  const isVisible = isOpen && type === 'download-output-modal';
  const modalData = isVisible && isDownloadOutputOverlayData(data) ? data : null;

  const downloadFolderName = modalData?.downloadFolderName ?? '';
  const downloadTargetTableId = modalData?.downloadTargetTableId ?? null;

  const handleClose = () => dispatch(closeOverlay());

  const { mutate: downloadDestinationFile, isPending: isDownloading } =
    useDownloadDestinationFileMutation({
      onSuccess: () => handleClose(),
    })

  const handleDownload = () => {
    if (!downloadFolderName || downloadTargetTableId == null) {
      toast.error('Download is not available for this pipeline.')
      return
    }

    downloadDestinationFile({ folderName: downloadFolderName, targetTableId: downloadTargetTableId })
  }

  return (
    <Dialog open={isVisible} onOpenChange={(open) => { if (!open) handleClose() }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle className={styles.modalTitle}>Download</DialogTitle>
        </DialogHeader>

        <DialogBody className='flex flex-col items-center gap-4'>
          <p className={styles.message}>
            Your Excel output is ready to download. Output Excel file is best suited for small data.
          </p>

          <DownloadIcon className='size-36' />

          <Button
            type="button"
            onClick={handleDownload}
            disabled={isDownloading}
          >
            {isDownloading ? 'Downloading...' : 'Download'}
          </Button>

        </DialogBody>
      </DialogContent>
    </Dialog>
  )
}

export default DownloadOutputModal
